import { useCallback, useRef, useLayoutEffect } from "react";
import Video, { LocalVideoTrack, LocalAudioTrack, LocalTrack } from "twilio-video";
import { i18n } from "@tellescope/i18n";
import { useVideoCalling } from "./useVideoCallingContext";
import type { VideoCallingSlice } from "../types";

function parseDeviceError(error: unknown): string {
  if (error instanceof Error) {
    const name = error.name;

    switch (name) {
      case "NotFoundError":
      case "DevicesNotFoundError":
        return i18n.t("No camera or microphone found. Please connect a device and try again.", { ns: "videoCalling" });
      case "NotAllowedError":
      case "PermissionDeniedError":
        return i18n.t("Camera and microphone access was denied. Please allow access in your browser settings.", {
          ns: "videoCalling",
        });
      case "NotReadableError":
      case "TrackStartError":
        return i18n.t("Your camera or microphone is already in use by another application.", { ns: "videoCalling" });
      case "OverconstrainedError":
      case "ConstraintNotSatisfiedError":
        return i18n.t("Your camera does not support the requested video settings.", { ns: "videoCalling" });
      case "TypeError":
        return i18n.t("Invalid media constraints. Please try again.", { ns: "videoCalling" });
      default:
        return error.message || i18n.t("Failed to access camera and microphone.", { ns: "videoCalling" });
    }
  }
  return i18n.t("An unknown error occurred while accessing your camera and microphone.", { ns: "videoCalling" });
}

/**
 * Hook for managing local video and audio tracks.
 *
 * Handles track creation, destruction, and toggling.
 * Uses the Twilio Video SDK for track operations.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { createLocalTracks, toggleVideo, toggleAudio } = useLocalTracks();
 *
 *   return (
 *     <div>
 *       <button onClick={createLocalTracks}>Start Camera</button>
 *       <button onClick={toggleVideo}>Toggle Video</button>
 *       <button onClick={toggleAudio}>Toggle Audio</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useLocalTracks() {
  const videoCalling = useVideoCalling();

  const localTracksRef = useRef<(LocalVideoTrack | LocalAudioTrack)[]>([]);

  // Use ref to access latest videoCalling without needing it as callback dependency
  // This prevents callbacks from being recreated on every state change
  const videoCallingRef = useRef<VideoCallingSlice>(videoCalling);
  useLayoutEffect(() => {
    videoCallingRef.current = videoCalling;
  });

  /**
   * Create local video and audio tracks.
   * Updates the Zustand store with the created tracks.
   * Sets deviceError in state if track creation fails.
   * Idempotent: returns existing tracks if already created.
   */
  const createLocalTracks = useCallback(async () => {
    const vc = videoCallingRef.current;

    // Return existing tracks if already created (idempotent)
    if (vc.localVideoTrack || vc.localAudioTrack) {
      return localTracksRef.current;
    }

    // Clear any previous device error
    vc.setDeviceError(null);

    try {
      const tracks = await Video.createLocalTracks({
        audio: true,
        video: { width: 640, height: 480, frameRate: 24 },
      });

      // Store refs for cleanup
      localTracksRef.current = tracks as (LocalVideoTrack | LocalAudioTrack)[];

      // Find and set tracks in store
      const videoTrack = tracks.find((track: LocalTrack) => track.kind === "video") as LocalVideoTrack | undefined;
      const audioTrack = tracks.find((track: LocalTrack) => track.kind === "audio") as LocalAudioTrack | undefined;

      if (videoTrack) {
        vc.setLocalVideoTrack(videoTrack);
        vc.setVideoEnabled(true);
      }
      if (audioTrack) {
        vc.setLocalAudioTrack(audioTrack);
        vc.setAudioEnabled(true);
      }

      return tracks;
    } catch (error) {
      const errorMessage = parseDeviceError(error);
      vc.setDeviceError(errorMessage);
      throw error;
    }
  }, []);

  /**
   * Destroy local tracks and clean up.
   */
  const destroyLocalTracks = useCallback(() => {
    const vc = videoCallingRef.current;

    // Stop tracks from ref
    localTracksRef.current.forEach((track) => {
      track.stop();
    });
    localTracksRef.current = [];

    // Also stop tracks from store (in case they differ)
    if (vc.localVideoTrack) {
      vc.localVideoTrack.stop();
    }
    if (vc.localAudioTrack) {
      vc.localAudioTrack.stop();
    }

    vc.setLocalVideoTrack(null);
    vc.setLocalAudioTrack(null);
  }, []);

  /**
   * Toggle video track enabled state.
   */
  const toggleVideo = useCallback(() => {
    const vc = videoCallingRef.current;
    const track = vc.localVideoTrack;
    if (track) {
      if (vc.isVideoEnabled) {
        track.disable();
        vc.setVideoEnabled(false);
      } else {
        track.enable();
        vc.setVideoEnabled(true);
      }
    }
  }, []);

  /**
   * Toggle audio track enabled state.
   */
  const toggleAudio = useCallback(() => {
    const vc = videoCallingRef.current;
    const track = vc.localAudioTrack;
    if (track) {
      if (vc.isAudioEnabled) {
        track.disable();
        vc.setAudioEnabled(false);
      } else {
        track.enable();
        vc.setAudioEnabled(true);
      }
    }
  }, []);

  return {
    // State (from Zustand)
    localVideoTrack: videoCalling.localVideoTrack,
    localAudioTrack: videoCalling.localAudioTrack,
    isVideoEnabled: videoCalling.isVideoEnabled,
    isAudioEnabled: videoCalling.isAudioEnabled,

    // Actions
    createLocalTracks,
    destroyLocalTracks,
    toggleVideo,
    toggleAudio,
  };
}
